import Two from 'two.js';
import { TEXT_POS, WHEEL_RADIUS } from 'rf-ui/GameRenderer/wheelOfFortuneConstants';
import { Group } from 'two.js/src/group';
import { ArcSegment } from 'two.js/src/shapes/arc-segment';
import { Text } from 'two.js/src/text';
import { Rectangle } from 'two.js/src/shapes/rectangle';
import TextTextureFactory from 'rf-ui/GameRenderer/TextTextureFactory';
import textureDots from '../../../assets/images/textures/wrap-dots.svg';
import textureLines from '../../../assets/images/textures/wrap-green-diagonal-lines.svg';
import textureRainbow from '../../../assets/images/textures/wrap-rainbow-stripes.svg';
import textureBoy from '../../../assets/images/textures/wrap-blue.svg';
import textureGirl from '../../../assets/images/textures/wrap-pink-girly.svg';
import textureFood from '../../../assets/images/textures/wrap-lightblue.svg';
import textureFlames from '../../../assets/images/textures/wrap-flames.svg';

const COLOR_NAME_FILL = '#FFFFFF';
export const WRAP_PATTERN_URLS = [
  textureDots,
  textureLines,
  textureRainbow,
  textureBoy,
  textureGirl,
  textureFood
];

export default class Ticket extends Group {
  private static wrapTextures: Two.Texture[] | null = null;

  private static flamesWrapTexture: Two.Texture | null = null;

  private two: Two;

  public background: ArcSegment;

  private nameTag: Rectangle;

  private nameTagTexture: Two.Texture | null = null;

  private nameTagBaseWidth: number = 0;

  private currentNameTagWidth: number = 0;

  private nameTagScale: number = 1;

  private nameTagLengthScale: number = 1;

  private massCenteredEnabled: boolean = false;

  public startAngle: number

  public width: number;

  public color: string

  public playerId?: string;

  constructor(
    two: Two,
    startAngle: number,
    width: number,
    color: string,
    name: string,
    stroke: boolean,
    playerId?: string
  ) {
    super();
    this.id = `Ticket-${name}`;
    this.two = two;
    this.startAngle = startAngle;
    this.width = width;
    this.color = color;
    this.playerId = playerId;
    this.background = this.createBackground(startAngle, width, this.getFill(), stroke);
    this.nameTag = this.createNameTag(startAngle, startAngle + width, name);

    this.add(this.background);
    if (this.nameTag) this.add(this.nameTag);
  }

  public destroy() {
    if (this.nameTag) {
      this.nameTag.fill = 'transparent';
      this.nameTag.stroke = 'none';
    }

    this.remove();
    this.two.release(this);
    this.nameTagTexture = null;

    // Drop direct shape refs after Two.js has been released.
    // @ts-ignore
    this.nameTag = null;
    // @ts-ignore
    this.background = null;
  }

  public setFlash(on: boolean) {
    if (on) {
      this.background.fill = COLOR_NAME_FILL;
    } else {
      this.background.fill = this.getFill();
      this.nameTag.opacity = 1;
      this.nameTag.scale = this.nameTagScale;
    }
  }

  public massCentered() {
    this.massCenteredEnabled = true;
    this.updateMassCenterTransform();
    this.updateNameTagTransform(this.startAngle, this.width);
    return this;
  }

  public resize(rotation: number, width: number) {
    this.background.startAngle = rotation;
    this.background.endAngle = rotation + width;

    this.width = width;
    this.startAngle = rotation;

    const wedgeSizeTextFactor = Math.min(1, width / (Math.PI * 0.075));
    this.nameTagScale = this.nameTagLengthScale * wedgeSizeTextFactor;
    this.currentNameTagWidth = this.nameTagBaseWidth * this.nameTagScale;
    this.updateMassCenterTransform();
    this.updateNameTagTransform(rotation, width);
  }

  public static getWrapTextures(two: Two) {
    if (!Ticket.wrapTextures) {
      Ticket.wrapTextures = WRAP_PATTERN_URLS.map((patternUrl) => {
        const texture = two.makeTexture(patternUrl);
        texture.repeat = 'repeat';
        return texture;
      });
    }

    return Ticket.wrapTextures;
  }

  public static getRandomWrapTexture(two: Two) {
    const wrapTextures = Ticket.getWrapTextures(two);
    return wrapTextures[Math.floor(Math.random() * wrapTextures.length)];
  }

  public static getFlamesWrapTexture(two: Two) {
    if (!Ticket.flamesWrapTexture) {
      Ticket.flamesWrapTexture = two.makeTexture(textureFlames);
      Ticket.flamesWrapTexture.repeat = 'repeat';
    }

    return Ticket.flamesWrapTexture;
  }

  protected getFill() {
    if (this.playerId === 'hysiA3WZS7UZ3vStLPP4twog7qi2') {
      return Ticket.getFlamesWrapTexture(this.two);
    }

    return this.color;
  }

  createBackground(rotation: number, width: number, fill: string | Two.Texture, stroke: boolean) {
    const arc = this.two.makeArcSegment(
      0,
      0,
      0,
      WHEEL_RADIUS,
      rotation,
      rotation + width,
    );
    arc.fill = fill;
    if (stroke) {
      arc.stroke = '#fff';
      arc.linewidth = 2;
    } else {
      arc.stroke = 'none';
    }
    return arc;
  }

  setTranslationShifted(x: number, y: number) {
    const shift = this.getMassCenterShift();

    this.translation.x = x + shift.x;
    this.translation.y = y + shift.y;
  }

  createTextNameTag(startTextAngle: number, endTextAngle: number, name: string) {
    const textAngle = startTextAngle + (endTextAngle - startTextAngle) / 2.0;
    const nameToRender = name.slice(0, 16);
    const text = new Two.Text(
      nameToRender,
      Math.cos(textAngle) * (WHEEL_RADIUS * TEXT_POS),
      Math.sin(textAngle) * (WHEEL_RADIUS * TEXT_POS)
    );
    const wedgeSizeTextFactor = Math.min(1, (endTextAngle - startTextAngle) / (Math.PI * 0.075));
    const nameLengthSizeFactor = Math.min(1, (1 - (nameToRender.length - 10) * 0.08));
    text.rotation = textAngle;
    text.fill = COLOR_NAME_FILL;
    text.alignment = 'right';
    text.size = WHEEL_RADIUS * 0.13 * wedgeSizeTextFactor * nameLengthSizeFactor;
    text.family = 'Roboto';
    return text;
  }

  convertTextToTexture(textTag: Text) {
    const nameToRender = `${textTag.value || ''}`;
    const family = textTag.family || 'Roboto';
    const cachedTexture = TextTextureFactory.getOrCreateNameTexture(this.two, nameToRender, family);
    this.nameTagTexture = cachedTexture.texture;
    this.nameTagScale = Number(textTag.size) / cachedTexture.fontSize;
    this.nameTagBaseWidth = cachedTexture.width;
    this.currentNameTagWidth = cachedTexture.width * this.nameTagScale;
    const initialWedgeSizeTextFactor = Math.min(1, this.width / (Math.PI * 0.075));
    this.nameTagLengthScale = initialWedgeSizeTextFactor > 0
      ? this.nameTagScale / initialWedgeSizeTextFactor
      : this.nameTagScale;

    const textSprite = this.two.makeRectangle(
      0,
      0,
      cachedTexture.width,
      cachedTexture.height
    );
    textSprite.fill = this.nameTagTexture;
    textSprite.stroke = 'none';
    this.nameTag = textSprite;
    this.updateNameTagTransform(this.startAngle, this.width);
    return textSprite;
  }

  createNameTag(startTextAngle: number, endTextAngle: number, name: string) {
    const textTag = this.createTextNameTag(startTextAngle, endTextAngle, name);
    return this.convertTextToTexture(textTag);
  }

  private updateNameTagTransform(rotation: number, width: number) {
    if (!this.nameTag) return;

    const shift = this.massCenteredEnabled ? this.getMassCenterShift(width, rotation) : { x: 0, y: 0 };
    const textAngle = rotation + (width / 2.0);
    this.nameTag.rotation = textAngle;
    this.nameTag.scale = this.nameTagScale;

    const anchorX = Math.cos(textAngle) * (WHEEL_RADIUS * TEXT_POS);
    const anchorY = Math.sin(textAngle) * (WHEEL_RADIUS * TEXT_POS);
    this.nameTag.translation.x = anchorX - (Math.cos(textAngle) * (this.currentNameTagWidth / 2)) - shift.x;
    this.nameTag.translation.y = anchorY - (Math.sin(textAngle) * (this.currentNameTagWidth / 2)) - shift.y;
  }

  private updateMassCenterTransform() {
    const shift = this.massCenteredEnabled ? this.getMassCenterShift() : { x: 0, y: 0 };
    this.background.translation.x = -shift.x;
    this.background.translation.y = -shift.y;
  }

  private getMassCenterShift(width: number = this.width, startAngle: number = this.startAngle) {
    const centerAngle = startAngle + (width / 2);
    const radiusToCenter = (WHEEL_RADIUS * 0.5) / Math.cos(width / 2);

    return {
      x: Math.cos(centerAngle) * radiusToCenter,
      y: Math.sin(centerAngle) * radiusToCenter
    };
  }
}
